import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Car, Check, ChevronDown, Factory, FlaskConical, Languages, LayoutGrid, MapPin, Menu, X } from "lucide-react";
import { languageOptions, useLanguage } from "./contexts/LanguageContext";

const navigationItems = [
  { key: "home", url: "/" },
  { key: "templates", url: "/templates" },
  { key: "distributors", url: "/distributors" },
  { key: "about", url: "/about" },
];

const productItems = [
  { key: "kylendar", url: "/kylendar", icon: CalendarDays },
  { key: "compare", url: "/products", icon: LayoutGrid },
  { key: "trevaron", url: "https://trevaron.cloud/", icon: Car, external: true },
];

export default function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isProductsOpen, setIsProductsOpen] = React.useState(false);
  const productsMenuRef = React.useRef(null);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const locationCopy = t("footer.locations", {});
  const isFirstLocation = React.useRef(true);
  const isProductsActive = location.pathname.startsWith("/products") || location.pathname.startsWith("/kylendar");

  React.useEffect(() => {
    if (location.hash) {
      isFirstLocation.current = false;
      return;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: isFirstLocation.current ? "auto" : "smooth",
    });
    isFirstLocation.current = false;
  }, [location.pathname, location.hash]);

  React.useEffect(() => {
    setIsProductsOpen(false);
    setIsMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!isProductsOpen) return undefined;

    function handlePointerDown(event) {
      if (!productsMenuRef.current?.contains(event.target)) setIsProductsOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setIsProductsOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProductsOpen]);

  function handleProductMenuToggle() {
    setIsProductsOpen((open) => !open);
  }

  function handleMobileProductToggle() {
    setIsProductsOpen((open) => !open);
  }

  return (
    <div className="min-h-screen bg-white">
      <header ref={productsMenuRef} className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-sm">
        <div className="relative mx-auto grid h-[68px] max-w-[1664px] grid-cols-[1fr_auto] items-center px-5 sm:px-8 md:h-[74px] lg:grid-cols-[1fr_auto_1fr] lg:px-10 2xl:px-16">
          <Link to="/" className="flex items-center space-x-3" onClick={() => setIsMenuOpen(false)}>
            <img src="/logo.png" alt={t("accessibility.logo")} className="h-10 w-auto object-contain md:h-12" />
            <span className="text-xl font-bold logo-text md:text-2xl">Intelevia</span>
          </Link>
          <nav className="hidden items-center gap-5 lg:flex xl:gap-8" aria-label={t("accessibility.primaryNavigation")}>
            <Link to="/" className={`flex h-full items-center border-b-2 text-sm font-semibold transition ${location.pathname === "/" ? "border-emerald-700 text-emerald-700" : "border-transparent text-slate-600 hover:text-emerald-700"}`}>
              {t("nav.home")}
            </Link>
            <div className="relative flex h-full items-center">
              <button type="button" aria-expanded={isProductsOpen} aria-haspopup="true" onClick={handleProductMenuToggle} className={`flex h-full items-center gap-1 border-b-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 ${isProductsActive ? "border-emerald-700 text-emerald-700" : "border-transparent text-slate-600 hover:text-emerald-700"}`}>
                {t("nav.products")} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isProductsOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {isProductsOpen && <div role="menu" aria-label={t("nav.products")} className="absolute left-1/2 top-[calc(100%_-_1px)] w-80 -translate-x-1/2 rounded-2xl border border-brand-100 bg-white p-2 shadow-[0_24px_50px_-28px_rgba(8,122,88,.55)]">
                {productItems.map((item) => <ProductMenuLink key={item.key} item={item} onSelect={() => setIsProductsOpen(false)} t={t} language={language} />)}
              </div>}
            </div>
            <a href="https://trevaron.cloud/" target="_blank" rel="noreferrer" className="flex h-full items-center gap-1 border-b-2 border-transparent text-sm font-semibold text-slate-600 transition hover:border-emerald-700 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2">
              {t("nav.trevaron")} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            {navigationItems.slice(1).map((item) => (
              <Link key={item.key} to={item.url} className={`flex h-full items-center border-b-2 text-sm font-semibold transition ${location.pathname === item.url ? "border-emerald-700 text-emerald-700" : "border-transparent text-slate-600 hover:text-emerald-700"}`}>
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </nav>
          <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center justify-end gap-2 sm:right-6 lg:right-10 2xl:right-16">
            <LanguageSelect language={language} setLanguage={setLanguage} t={t} />
            <button type="button" onClick={() => setIsMenuOpen((open) => !open)} className="rounded-md p-2 text-slate-700 hover:bg-emerald-50 lg:hidden" aria-label={isMenuOpen ? t("accessibility.closeNavigation") : t("accessibility.openNavigation")}>
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {isMenuOpen && <nav className="border-t border-slate-200 bg-white px-5 py-4 lg:hidden" aria-label={t("accessibility.mobileNavigation")}>
          <div className="flex flex-col gap-1">
            {navigationItems.slice(0, 1).map((item) => <Link key={item.key} to={item.url} onClick={() => setIsMenuOpen(false)} className="rounded-md px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700">{t(`nav.${item.key}`)}</Link>)}
            <button type="button" aria-expanded={isProductsOpen} onClick={handleMobileProductToggle} className={`flex items-center justify-between rounded-md px-3 py-3 text-left text-sm font-semibold transition ${isProductsActive ? "bg-brand-50 text-emerald-700" : "text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"}`}>
              <span>{t("nav.products")}</span><ChevronDown className={`h-4 w-4 transition-transform ${isProductsOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {isProductsOpen && <div role="menu" aria-label={t("accessibility.mobileProducts")} className="ml-3 flex flex-col gap-1 border-l border-brand-100 pl-2">{productItems.map((item) => <ProductMenuLink key={item.key} item={item} onSelect={() => { setIsProductsOpen(false); setIsMenuOpen(false); }} mobile t={t} language={language} />)}</div>}
            <a href="https://trevaron.cloud/" target="_blank" rel="noreferrer" onClick={() => setIsMenuOpen(false)} className="inline-flex items-center gap-2 rounded-md px-3 py-3 text-sm font-semibold text-slate-700 transition hover:bg-emerald-50 hover:text-emerald-700">{t("nav.trevaron")} <ArrowUpRight className="h-4 w-4" aria-hidden="true" /></a>
            {navigationItems.slice(1).map((item) => <Link key={item.key} to={item.url} onClick={() => setIsMenuOpen(false)} className="rounded-md px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700">{t(`nav.${item.key}`)}</Link>)}
          </div>
        </nav>}
      </header>
      <div className="pt-[68px] md:pt-[74px]">{children}</div>
      <footer className="relative overflow-hidden border-t border-brand-200/70 bg-gradient-to-br from-[#0a7057] via-[#075f4d] to-[#06483f] text-white">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-brand-200/20 to-transparent" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-32 -top-40 h-96 w-96 rounded-full bg-brand-300/25 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-48 left-1/3 h-96 w-96 rounded-full bg-emerald-950/25 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:px-10 lg:py-16">
          <div className="grid gap-12 md:grid-cols-[1.45fr_0.8fr_0.8fr_0.8fr] lg:gap-16">
            <div>
              <Link to="/" className="inline-flex items-center gap-3">
                <img src="/logo.png" alt={t("accessibility.logo")} className="h-11 w-auto object-contain brightness-0 invert" />
                <span className="font-display text-2xl font-bold tracking-[-0.04em] text-white">Intelevia</span>
              </Link>
              <p className="mt-5 max-w-sm text-sm leading-6 text-emerald-50/75">{t("footer.description")}</p>
            </div>
            <FooterColumn title={t("footer.products")} links={[["nav.kylendar", "/kylendar"], ["nav.trevaron", "https://trevaron.cloud/", true], ["footer.compare", "/products"], ["nav.templates", "/templates"]]} t={t} />
            <FooterColumn title={t("footer.company")} links={[["nav.home", "/"], ["nav.distributors", "/distributors"], ["nav.about", "/about"], ["footer.contact", "/contact"]]} t={t} />
            <FooterColumn title={t("footer.support")} links={[["footer.help", "/help-center"], ["footer.privacy", "/privacy-policy"]]} t={t} />
          </div>

          <div className="mt-14 border-t border-emerald-200/20 pt-12">
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-200">{t("footer.globalPresence")}</p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-[-0.04em] text-white sm:text-3xl">{t("footer.globalTitle")}</h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-emerald-50/65">{t("footer.globalBody")}</p>
            </div>
            <div className="mt-8 grid gap-4 lg:grid-cols-3">
              <PresenceCard icon={MapPin} title={t("footer.usCenter")} lines={locationCopy.us} />
              <PresenceCard icon={FlaskConical} title={t("footer.rdCenter")} lines={locationCopy.rd} />
              <PresenceCard icon={Factory} title={t("footer.plant")} lines={locationCopy.plant} />
            </div>
          </div>

          <div className="mt-12 flex flex-col gap-4 border-t border-emerald-200/20 pt-6 text-xs text-emerald-50/60 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p>{t("footer.operatedBy")}</p>
              <p className="mt-1">{t("footer.rights")}</p>
            </div>
            <Link to="/privacy-policy" className="font-semibold transition hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:ring-offset-2 focus:ring-offset-[#075f4d]">{t("footer.privacy")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterColumn({ title, links, t }) {
  return <div><h2 className="font-display text-sm font-bold tracking-[-0.02em] text-white">{title}</h2><ul className="mt-5 space-y-3">{links.map(([label, to, external]) => <li key={label}>{external ? <a href={to} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-emerald-50/80 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:ring-offset-2 focus:ring-offset-[#075f4d]"><span className="h-1.5 w-1.5 rounded-full bg-brand-200" aria-hidden="true" />{t(label)}</a> : <Link to={to} className="inline-flex items-center gap-2 text-sm text-emerald-50/80 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:ring-offset-2 focus:ring-offset-[#075f4d]"><span className="h-1.5 w-1.5 rounded-full bg-brand-200" aria-hidden="true" />{t(label)}</Link>}</li>)}</ul></div>;
}

function ProductMenuLink({ item, onSelect, mobile = false, t, language }) {
  const Icon = item.icon;
  const itemCopy = item.key === "compare" ? { label: t("interface.compareLabel"), description: t("interface.compareDescription"), detail: '4.2" / 7.5"' } : null;
  const className = `group flex items-start gap-3 rounded-xl p-3 transition hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-inset ${mobile ? "rounded-md" : ""}`;
  const content = <><span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 transition group-hover:bg-white"><Icon className="h-4 w-4" aria-hidden="true" /></span><span className="min-w-0"><span className="flex items-center justify-between gap-3"><span className="text-sm font-bold text-slate-900">{itemCopy?.label || t(`nav.${item.key}`)}</span>{item.external ? <ArrowUpRight className="h-3.5 w-3.5 text-brand-700" aria-hidden="true" /> : null}</span><span className="mt-0.5 block text-xs leading-5 text-slate-500">{itemCopy?.description || t(`nav.${item.key}Description`)}</span><span className="mt-1 block text-[10px] font-bold uppercase tracking-[.12em] text-brand-700">{itemCopy?.detail || t(`nav.${item.key}Detail`)}</span></span></>;

  if (item.external) return <a role="menuitem" href={item.url} target="_blank" rel="noreferrer" onClick={onSelect} className={className}>{content}</a>;
  return <Link role="menuitem" to={item.url} onClick={onSelect} className={className}>{content}</Link>;
}

function LanguageSelect({ language, setLanguage, t }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const optionRefs = React.useRef([]);
  const selectedOption = languageOptions.find((option) => option.value === language) || languageOptions[0];

  React.useEffect(() => {
    if (!isOpen) return undefined;

    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  function focusOption(index) {
    const nextIndex = (index + languageOptions.length) % languageOptions.length;
    optionRefs.current[nextIndex]?.focus();
  }

  function handleTriggerKeyDown(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      window.requestAnimationFrame(() => focusOption(event.key === "ArrowDown" ? 0 : languageOptions.length - 1));
    }
  }

  function handleOptionKeyDown(event, index) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(languageOptions.length - 1);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  }

  function handleSelect(value) {
    setLanguage(value);
    setIsOpen(false);
    triggerRef.current?.focus();
  }

  return <div ref={containerRef} className="relative">
    <button
      ref={triggerRef}
      type="button"
      aria-label={t("language.label")}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      onClick={() => setIsOpen((open) => !open)}
      onKeyDown={handleTriggerKeyDown}
      className={`inline-flex min-w-[8.5rem] items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-sm transition hover:border-brand-300 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 ${isOpen ? "border-brand-500 bg-brand-50/60 text-brand-800" : "border-slate-200"}`}
    >
      <span className="inline-flex items-center gap-1.5"><Languages className="h-4 w-4 text-brand-700" aria-hidden="true" />{selectedOption.label}</span>
      <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 ${isOpen ? "rotate-180 text-brand-700" : ""}`} aria-hidden="true" />
    </button>
    <div role="listbox" aria-hidden={!isOpen} aria-label={t("language.label")} className={`absolute left-1/2 top-[calc(100%+0.6rem)] z-50 w-52 -translate-x-1/2 overflow-hidden rounded-2xl border border-brand-100 bg-white p-1.5 shadow-[0_22px_46px_-24px_rgba(8,122,88,.55)] ring-1 ring-brand-50 transition-[opacity,transform,visibility] duration-200 ease-out ${isOpen ? "visible translate-y-0 scale-100 opacity-100" : "invisible pointer-events-none translate-y-2 scale-[.98] opacity-0"}`}>
      <p className="px-3 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{t("language.label")}</p>
      {languageOptions.map((option, index) => <button
        key={option.value}
        ref={(element) => { optionRefs.current[index] = element; }}
        type="button"
        role="option"
        aria-selected={language === option.value}
        onClick={() => handleSelect(option.value)}
        onKeyDown={(event) => handleOptionKeyDown(event, index)}
        className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-brand focus:ring-inset ${language === option.value ? "bg-brand-50 text-brand-800" : "text-slate-600 hover:bg-emerald-50 hover:text-brand-800"}`}
      >
        {option.label}
        {language === option.value ? <Check className="h-4 w-4 text-brand-700" aria-hidden="true" /> : null}
      </button>)}
    </div>
  </div>;
}

function PresenceCard({ icon: Icon, title, lines }) {
  return <article className="rounded-2xl border border-emerald-100/20 bg-white/[0.045] p-5 shadow-[0_18px_40px_-34px_rgba(2,44,34,.75)] transition hover:border-brand-200/55 hover:bg-white/[0.07]"><div className="flex items-center gap-3"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-500/45 text-brand-50"><Icon className="h-5 w-5" aria-hidden="true" /></span><h3 className="font-display text-base font-bold leading-5 text-brand-50">{title}</h3></div><address className="mt-5 space-y-1 text-sm not-italic leading-6 text-emerald-50/75">{lines.map((line) => <p key={line}>{line}</p>)}</address></article>;
}
